import 'regenerator-runtime/runtime';
import { createRoot } from 'react-dom/client';
import { applyMiddleware, compose, createStore } from 'redux';
import 'moment/locale/de';
import { setI18n } from 'react-i18next';
import React from 'react';
import 'core-js/stable';
import { BrowserRouter } from 'react-router-dom';
import thunk from 'redux-thunk';
import queryString from 'query-string';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import '../node_modules/react-image-crop/dist/ReactCrop.css';

import rootReducer from './rootReducer';
import {
  authTokenUsedButNoManageMeSetRedirect,
  getAvailableSystemLanguages,
  getDefaultSystemLanguage,
  getSystemLanguage,
  redeemTokenReturnedTokenErrorRedirect,
  getBrowserLanguage,
} from './services/session';
import apiMW from './services/apiMiddleware';
import { subscribeContentLanguageToStore } from './services/translationServices';
import translations from './services/translationInit';
import App from './App';
import logger from './services/logger/LlsLogger';
import { UserPermissions } from './interfaces/session.types';
import './index.css';
import mainTheme from './styles/theme';
import { logout, initUserLogin, redeemSessionToken } from './store/session/actions';

const composeEnhancers: typeof compose = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
export const store = createStore(rootReducer, composeEnhancers(applyMiddleware(thunk, apiMW)));

// --______________________________________________________-->
// Global auth_token catch
const { auth_token } = queryString.parse(document.location.search) as { auth_token?: string };
const path = document.location.pathname;

const tryRedeemToken = async () => {
  const data = await store.dispatch(redeemSessionToken(auth_token));
  if (!data) {
    localStorage.removeItem('jwt');
    return null;
  }
  localStorage.setItem('jwt', data.token);
  return data.token;
};

// --______________________________________________________-->
// lets go and start the app
function startApp() {
  let initialSystemLanguage = getSystemLanguage(store.getState());
  if (!localStorage.getItem('jwt')) {
    initialSystemLanguage = getBrowserLanguage();
  }
  const defaultSystemLanguage = getDefaultSystemLanguage(store.getState());
  const initialAvailableLanguage = getAvailableSystemLanguages(store.getState());
  if (import.meta.hot && import.meta.env.PROD) {
    // Enable Webpack hot module replacement for reducers
    import.meta.hot.accept('./rootReducer', () => {
      const nextRootReducer = require('./rootReducer');
      store.replaceReducer(nextRootReducer);
    });
  }

  subscribeContentLanguageToStore(store);
  translations
    .init(initialAvailableLanguage, initialSystemLanguage, defaultSystemLanguage, store)
    .then((i18n) => {
      setI18n(i18n);
      const rootElement = document.getElementById('root') as HTMLElement
      createRoot(rootElement).render(React.createElement(
        Provider,
        { store },
        React.createElement(
          BrowserRouter, {}, React.createElement(
            ThemeProvider, { theme: mainTheme }, React.createElement(App))),
      ));
    });
}

// --______________________________________________________-->
// local jwt token available pre-check
function initApp() {
  const token = localStorage.getItem('jwt');
  if (token) {
    store
      .dispatch(initUserLogin(token))
      .then(response => {
        /* if the user does not have any permissions here we need to redirect him to the set
        password route
        with a special wording and redirect to the original location afterwards */
        const activated =
          response[1].sessionUserResp.hasOwnProperty('userPermissions') &&
          response[1].sessionUserResp.userPermissions.length > 0 &&
          response[1].sessionUserResp.userPermissions.includes(UserPermissions.ManageMe);
        if (path !== '/password' && path !== '/activate' && !activated) {
          store.dispatch(logout);
          window.location.href = authTokenUsedButNoManageMeSetRedirect(document.location);
          return;
        }

        startApp();
      })
      .catch((error) => {
        localStorage.removeItem('jwt');
        startApp();
        logger.error('Token error', {
          error,
          info: {
            componentStack: 'index.ts',
          },
        });
      });
  } else {
    startApp();
  }
}

if (auth_token && path !== '/password' && path !== '/activate') {
  tryRedeemToken()
    .then(initApp)
    .catch(() => {
      // in case of an error on redeeming the token, remove the token from the url params and
      // redirect to the login with the new redirect param
      window.location.href = redeemTokenReturnedTokenErrorRedirect(document.location);
      return true;
    });
} else {
  initApp();
}
